import React, { useCallback, useRef } from 'react';
import { Paperclip, Smile, Send } from 'lucide-react';

/**
 * Message composer: text input, attachment button, emoji placeholder, send.
 * Optional onTyping callback (debounced) for typing indicators.
 * onAttach(files): called when user selects files via attach button.
 * hasAttachments: allow send when only attachments (no text).
 */
export default function MessageInput({
  value,
  onChange,
  onSend,
  onTyping,
  onAttach,
  hasAttachments,
  disabled,
  placeholder = 'Write a message...',
}) {
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const handleAttachClick = useCallback(() => {
    if (!onAttach) return;
    fileInputRef.current?.click();
  }, [onAttach]);

  const handleFileChange = useCallback(
    (e) => {
      const files = e.target.files;
      if (files?.length && onAttach) onAttach(files);
      e.target.value = '';
    },
    [onAttach]
  );

  const handleChange = useCallback(
    (e) => {
      onChange?.(e.target.value);
      if (onTyping) {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => onTyping(), 300);
      }
    },
    [onChange, onTyping]
  );

  const handleSubmit = useCallback(() => {
    if ((!value?.trim() && !hasAttachments) || disabled) return;
    onSend?.();
    onChange?.('');
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [value, hasAttachments, disabled, onSend, onChange]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <div className="flex-shrink-0 p-4 border-t border-gray-200">
      <div className="flex items-end gap-2">
        {onAttach && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
              accept="image/*,.pdf,.doc,.docx,.txt"
            />
            <button
              type="button"
              onClick={handleAttachClick}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              aria-label="Attach file"
            >
              <Paperclip className="w-5 h-5 text-gray-600" />
            </button>
          </>
        )}
        <textarea
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={disabled}
          className="flex-1 min-h-[100px] max-h-60 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none disabled:opacity-50"
        />
        <button
          type="button"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          aria-label="Emoji"
        >
          <Smile className="w-5 h-5 text-gray-600" />
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={(!value?.trim() && !hasAttachments) || disabled}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
        >
          <Send className="w-4 h-4" />
          Send
        </button>
      </div>
    </div>
  );
}
